'use client';

import { forwardRef, ComponentRef, ComponentPropsWithoutRef, ReactNode } from 'react';
import { Root, Portal, Overlay, Close, Content, Title, Description } from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import closeIcon from '@/assets/icons/close.svg';
import alertCircleIcon from '@/assets/icons/alert-circle.svg';
import styles from './ConfirmationDialog.module.css';

const Dialog = Root;
const DialogPortal = Portal;
const DialogClose = Close;

const DialogOverlay = forwardRef<ComponentRef<typeof Overlay>, ComponentPropsWithoutRef<typeof Overlay>>(
  ({ className, ...props }, ref) => (
    <Overlay ref={ref} className={`${styles.overlay} ${className || ''}`} {...props} />
  )
);
DialogOverlay.displayName = Overlay.displayName;

const DialogContent = forwardRef<ComponentRef<typeof Content>, ComponentPropsWithoutRef<typeof Content>>(
  ({ className, children, ...props }, ref) => (
    <DialogPortal>
      <DialogOverlay />
      <Content ref={ref} className={`${styles.content} ${className || ''}`} {...props}>
        {children}
      </Content>
    </DialogPortal>
  )
);
DialogContent.displayName = Content.displayName;

interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  variant?: 'danger' | 'default';
  icon?: ReactNode;
  /** Show the alert icon on a non-danger dialog (danger always shows it). */
  withIcon?: boolean;
  /** Drop the cancel button, for a dialog that only has to be acknowledged. */
  hideCancel?: boolean;
  /**
   * Why the action just failed, shown inside the dialog so the coordinator can read it and try
   * again or step back - rather than the dialog closing on a failure as if it had worked.
   */
  error?: string | null;
  /** A field the confirmation needs, under the message - e.g. a reason (HAT-1441). */
  children?: ReactNode;
}

const ConfirmationDialog = ({
  open,
  onOpenChange,
  title,
  message,
  confirmText = 'confirm',
  cancelText = 'cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  variant = 'default',
  icon,
  withIcon = false,
  hideCancel = false,
  error,
  children,
}: ConfirmationDialogProps) => {
  const { t } = useTranslation();

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  const showIcon = variant === 'danger' || withIcon || icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={message ? 'confirmation-description' : undefined}>
        <DialogClose className={styles.closeButton}>
          <img src={closeIcon} alt="" />
        </DialogClose>

        <div className={styles.body}>
          {showIcon && (
            icon ? icon : <img src={alertCircleIcon} alt="" className={styles.icon} />
          )}

          <div className={styles.textContent}>
            <Title className={styles.title}>{title}</Title>

            {/* ph-mask: הודעות האישור מצטטות את מי שהפעולה חלה עליו (שם, כתובת דואר) -
                ממוסך בהקלטות PostHog, כמו שאר משטחי הדיאלוג (HAT-1081). */}
            {message && (
              <Description id="confirmation-description" className={`${styles.message} ph-mask`}>
                {message}
              </Description>
            )}

            {children}

            {error && (
              <p role="alert" className={`${styles.error} ph-mask`}>
                {error}
              </p>
            )}
          </div>

          <div className={styles.actions}>
            <button
              className={`${styles.button} ${variant === 'danger' ? styles.dangerButton : styles.primaryButton}`}
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? t('loading') : t(confirmText)}
            </button>
            {!hideCancel && (
              <button
                className={`${styles.button} ${styles.secondaryButton}`}
                onClick={handleCancel}
                disabled={isLoading}
              >
                {t(cancelText)}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationDialog;
